import React from "react";
import { useSelector } from "react-redux";
import {useLocation, useNavigate } from "react-router-dom";

import { Button, Header, Title, CardList, Modal, Breadcrumbs } from "../../components";

import { useAppDispatch } from "../../redux/store";
import { getCardSelector } from "../../redux/profile/cards/selectors";
import { setShouldSaveCard } from "../../redux/profile/cards/slice";
import { cartSelector } from "../../redux/cart/selectors";
import { accessTokenSelector } from "../../redux/auth/selectors";
import { partnerIdSelector, userDataSelector } from "../../redux/user/selectors";
import { sendBitrixPrepaymentInfo, createPaymentObject } from "../../redux/payment/bitrixPayment";
import { convertCartToBitrixItems } from "../../redux/payment/paymentHelpers";
import { fetchGetCards } from "../../redux/profile/cards/asyncActions";

import styles from "./Payment.module.scss";

import {
  getTotalPriceFromStorage,
  removeDateFromStorage,
  removeLastSelectedHallFromStorage,
  removeSubscrFormsDataFromStorage,
  removeSelectedAdultSubscrFromStorage,
  removeSelectedKidSubscrFromStorage,
} from "../../utils";
import { sendFrontendLog } from "../../redux/frontendLog/asyncActions";
import { getPaymentDescription } from "../../helpers/paymentTypes";
import { createTinkoffPaymentLink } from "../../helpers/createTinkoffPaymentLink";
import { getPaymentStatusAny as getPaymentInfo } from "../../helpers/getPaymentStatusAny";

import { PaymentPropsType } from "./types";
import { ModalTexts } from "../../resources/locale";
import { resetDate, resetLastSelectedHall } from "../../redux/halls/slice";
import {
  resetSelectedAdultSubscr,
  resetSelectedKidSubscr,
  resetSubscrFormsData,
} from "../../redux/subscriptions/slice";

export const Payment: React.FC<PaymentPropsType> = ({ title }) => {
  const { cardId, cards, shouldSaveCard } = useSelector(getCardSelector);
  const { basket, acceptedNotices } = useSelector(cartSelector);
  const accessToken = useSelector(accessTokenSelector);
  const partnerId = useSelector(partnerIdSelector);
  const user = useSelector(userDataSelector);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [totalPrice, setTotalPrice] = React.useState<number>();
  const [methodSelected, setMethodSelected] = React.useState<boolean>(false);
  const [isOpenModalPaymentError, setIsOpenModalPaymentError] = React.useState(false);
  const [paymentErrorMessage, setPaymentErrorMessage] = React.useState("");
  const [paymentId, setPaymentId] = React.useState<string>("");
  const [guid, setGuid] = React.useState<string>("");
  const [polling, setPolling] = React.useState(false);
  const [successProcessed, setSuccessProcessed] = React.useState(false);
  const [errorProcessed, setErrorProcessed] = React.useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const location = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  React.useEffect(() => {
    const storageTotalPrice = getTotalPriceFromStorage();
    if (storageTotalPrice) setTotalPrice(storageTotalPrice);
    dispatch(setShouldSaveCard(false));
  }, [dispatch]);

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data && event.data.type === 'CARD_ADDED') {
        dispatch(fetchGetCards());
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (!polling || !paymentId || successProcessed || errorProcessed) return;

    let attempts = 0;
    const MAX_ATTEMPTS = 150;

    const checkStatus = async () => {
      attempts++;

      if (attempts % 5 === 0) {
        try {
          dispatch(sendFrontendLog({
            type: "Payment status polling check",
            data: {
              component: "Payment/polling",
              timestamp: new Date().toISOString(),
              attempts,
              maxAttempts: MAX_ATTEMPTS,
              paymentId,
            },
          }));
        } catch (logError) {
        }
      }

      try {
        const paymentInfo = await getPaymentInfo(paymentId);
        const status = paymentInfo?.status;

        if (status === 'succeeded') {
          setPolling(false);
          setSuccessProcessed(true);
          
          dispatch(sendFrontendLog({
            type: "Payment succeeded via polling",
            data: {
              component: "Payment/polling",
              timestamp: new Date().toISOString(),
              attempts,
              paymentId,
              paymentInfo,
            },
          }));
          
          navigate("/payment-success", { state: { paymentId } });
        } else if (status === 'canceled' || status === 'cancelled') {
          setPolling(false);
          setErrorProcessed(true);
          
          dispatch(sendFrontendLog({
            type: "Payment canceled",
            data: {
              component: "Payment/polling",
              timestamp: new Date().toISOString(),
              attempts,
              paymentId,
              paymentInfo,
              cancellationDetails: paymentInfo?.cancellation_details,
            },
          }));
          
          navigate("/payment-error", { state: 'Платеж отменен' });
        }

        if (attempts >= MAX_ATTEMPTS) {
          setPolling(false);
          setErrorProcessed(true);
          
          dispatch(sendFrontendLog({
            type: "Payment polling timeout",
            data: {
              component: "Payment/polling",
              timestamp: new Date().toISOString(),
              attempts,
              maxAttempts: MAX_ATTEMPTS,
              paymentId,
            },
          }));
          
          navigate("/payment-error", { state: 'Превышено время ожидания оплаты' });
        }
      } catch (error: any) {
        dispatch(sendFrontendLog({
          type: "Payment status check error",
          data: {
            component: "Payment/polling",
            timestamp: new Date().toISOString(),
            attempts,
            paymentId,
            error: error?.response?.data || error?.message || String(error),
            errorResponse: error?.response?.data,
          },
        }));
        
        if (attempts >= MAX_ATTEMPTS) {
          setPolling(false);
          setErrorProcessed(true);
          navigate("/payment-error", { state: 'Ошибка проверки статуса платежа' });
        }
      }
    };

    checkStatus();

    const interval = setInterval(checkStatus, 2000);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!successProcessed && !errorProcessed) {
        setPolling(false);
        setErrorProcessed(true);
        
        dispatch(sendFrontendLog({
          type: "Payment polling global timeout",
          data: {
            component: "Payment/polling",
            timestamp: new Date().toISOString(),
            maxAttempts: MAX_ATTEMPTS,
            paymentId,
          },
        }));
        
        navigate("/payment-error", { state: 'Превышено время ожидания оплаты' });
      }
    }, MAX_ATTEMPTS * 2000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [polling, paymentId, successProcessed, errorProcessed, navigate, dispatch]);

  const handleStartPayment = async () => {
    if (!accessToken) {
      setPaymentErrorMessage("Требуется авторизация");
      setIsOpenModalPaymentError(true);
      return;
    }

    if (!totalPrice || !partnerId) {
      setPaymentErrorMessage("Отсутствуют необходимые данные для оплаты");
      setIsOpenModalPaymentError(true);
      return;
    }

    try {
      dispatch(sendFrontendLog({
        type: "Payment process started",
        data: {
          component: "Payment/handleStartPayment",
          timestamp: new Date().toISOString(),
          url: window.location.pathname,
          userAgent: navigator.userAgent,
          totalPrice,
          cardId,
          hasCard: cardId !== 0,
          basketItemsCount: Object.keys(basket).length,
          basket: basket,
          partnerId,
          phone: user?.phone,
          // Объявления, подтверждённые галочками в корзине (напр. о закрытии бассейна)
          acceptedNotices: acceptedNotices || [],
        },
      }));
    } catch (logError) {
    }

    setIsLoading(true);

    const phone = user?.phone || '';
    
    if (!phone) {
      setPaymentErrorMessage("Не удалось получить номер телефона пользователя");
      setIsOpenModalPaymentError(true);
      setIsLoading(false);
      return;
    }

    const bitrixItems = convertCartToBitrixItems(basket, String(partnerId));
    
    const description = getPaymentDescription(bitrixItems);

    try {
      dispatch(sendFrontendLog({
        type: "Creating Tinkoff payment link",
        data: {
          component: "Payment/handleStartPayment",
          timestamp: new Date().toISOString(),
          totalPrice,
          phone,
          description,
          bitrixItems,
          partnerId: String(partnerId),
        },
      }));
    } catch (logError) {
    }

    try {
      // Единственный провайдер — Т-Касса. Привязанных карт у неё пока нет
      // (нужны CustomerKey + Recurrent + Rebill), поэтому всегда новая карта;
      // выбор сохранённой карты на странице оплаты убран (см. CardList).
      const result = await createTinkoffPaymentLink({
        totalPrice,
        phone,
        description,
        cardId: null,
        items: bitrixItems,
        partnerId: String(partnerId),
        savePaymentMethod: false,
      });

      if (!result.success) {
        dispatch(sendFrontendLog({
          type: "Payment creation failed",
          data: {
            component: "Payment/handleStartPayment",
            timestamp: new Date().toISOString(),
            error: result.error,
            errorMessage: result.errorMessage,
            totalPrice,
            phone,
            description,
            cardId,
            bitrixItems,
            partnerId: String(partnerId),
          },
        }));

        setIsLoading(false);
        setPaymentErrorMessage(result.errorMessage || "Ошибка создания платежа");
        setIsOpenModalPaymentError(true);
        return;
      }

      const receivedPaymentId = result.paymentId!;
      const receivedGuid = result.guid!;
      
      setPaymentId(receivedPaymentId);
      setGuid(receivedGuid);

      dispatch(sendFrontendLog({
        type: "Payment created successfully",
        data: {
          component: "Payment/handleStartPayment",
          timestamp: new Date().toISOString(),
          paymentId: receivedPaymentId,
          guid: receivedGuid,
          confirmationUrl: result.confirmationUrl,
          hasConfirmationUrl: !!result.confirmationUrl,
          hasSavedCard: result.hasSavedCard,
          status: result.status,
          totalPrice,
          phone,
          description,
          cardId,
          bitrixItems,
          partnerId: String(partnerId),
          acceptedNotices: acceptedNotices || [],
        },
      }));

      if (partnerId && receivedPaymentId) {
        try {
          const paymentObject = createPaymentObject(String(partnerId), receivedPaymentId, bitrixItems);

          dispatch(sendFrontendLog({
            type: "Sending prepayment info to Bitrix",
            data: {
              component: "Payment/handleStartPayment",
              timestamp: new Date().toISOString(),
              paymentId: receivedPaymentId,
              paymentObject,
              bitrixItems,
              itemsCount: bitrixItems.length,
              partnerId: String(partnerId),
            },
          }));

          await sendBitrixPrepaymentInfo({
            paymentObject,
            paymentId: receivedPaymentId,
          });

          dispatch(sendFrontendLog({
            type: "Prepayment info sent to Bitrix successfully",
            data: {
              component: "Payment/handleStartPayment",
              timestamp: new Date().toISOString(),
              paymentId: receivedPaymentId,
              paymentObject,
              bitrixItems,
              partnerId: String(partnerId),
            },
          }));
        } catch (bitrixError: any) {

          dispatch(sendFrontendLog({
            type: "Bitrix prepayment error",
            data: {
              component: "Payment/handleStartPayment",
              timestamp: new Date().toISOString(),
              error: bitrixError?.message || String(bitrixError),
              errorResponse: bitrixError?.response?.data,
              paymentId: receivedPaymentId,
              bitrixItems,
              partnerId: String(partnerId),
            },
          }));
        }
      }

      setIsLoading(false);

      // У Т-Кассы всегда уходим на её форму: мгновенного списания по
      // сохранённому токену здесь нет.
      if (result.confirmationUrl) {
        localStorage.setItem('lastPaymentId', receivedPaymentId);
        localStorage.setItem('lastPaymentGuid', receivedGuid);

        window.open(result.confirmationUrl, '_blank');
        navigate('/payment/confirm');

        dispatch(resetDate());
        removeDateFromStorage();
        dispatch(resetLastSelectedHall());
        removeLastSelectedHallFromStorage();
        dispatch(resetSubscrFormsData());
        removeSubscrFormsDataFromStorage();
        dispatch(resetSelectedAdultSubscr());
        removeSelectedAdultSubscrFromStorage();
        dispatch(resetSelectedKidSubscr());
        removeSelectedKidSubscrFromStorage();        
      } else {
        // Т-Касса всегда отдаёт PaymentURL. Если его нет — что-то не так на её
        // стороне; раньше здесь была ветка мгновенного списания по карте ЮKassa,
        // теперь молчать нельзя, иначе человек останется на странице без реакции.
        dispatch(sendFrontendLog({
          type: "Tinkoff payment link missing",
          data: {
            component: "Payment/handleStartPayment",
            timestamp: new Date().toISOString(),
            paymentId: receivedPaymentId,
            guid: receivedGuid,
            totalPrice,
            status: result.status,
            bitrixItems,
            partnerId: String(partnerId),
          },
        }));

        setPaymentErrorMessage(
          "Не удалось открыть форму оплаты. Попробуйте ещё раз или свяжитесь с клубом."
        );
        setIsOpenModalPaymentError(true);
      }
    } catch (error: any) {
      setIsLoading(false);
      
      dispatch(sendFrontendLog({
        type: "Payment process error",
        data: {
          component: "Payment/handleStartPayment",
          timestamp: new Date().toISOString(),
          error: error?.message || String(error),
          errorResponse: error?.response?.data,
          totalPrice,
          phone,
          cardId,
          bitrixItems,
          partnerId: String(partnerId),
        },
      }));
      
      setPaymentErrorMessage(
        "Что-то пошло не так. Попробуйте позже или свяжитесь с нами"
      );
      setIsOpenModalPaymentError(true);
    }
  };

  const handleSelectCard = () => {
    setMethodSelected(true);
    if (cardId !== 0) {
      dispatch(setShouldSaveCard(false));
    }
  };
  const handleCloseModalPayemntError = () => setIsOpenModalPaymentError(false);

  return (
    <>
      <Header title={title} />
      <main>
        <section className="container">
          <Breadcrumbs />
          <Title>{title}</Title>
          
          <div className={styles.paymentContent}>
            <div className={styles.paymentSection}>
              <h2 className={styles.sectionTitle}>Выберите способ оплаты</h2>
              <CardList onPage={"payment"} onClickFunc={handleSelectCard} />
            </div>
            
            <div className={styles.paymentActions}>
              <Button
                newStyles={styles.button}
                onClickFunc={handleStartPayment}
                disabled={isLoading || !methodSelected || polling}
              >
                <span>Оплатить {totalPrice ? `${totalPrice}₽` : ''}</span>
              </Button>
              {isLoading && (
                <div className={styles.loadingMessage}>Данные отправляются...</div>
              )}
              {polling && (
                <div className={styles.loadingMessage}>Проверка статуса платежа...</div>
              )}
            </div>
          </div>
        </section>
      </main>

      {isOpenModalPaymentError ? (
        <Modal
          title={ModalTexts.cart.paymentError.TITLE}
          subTitle={paymentErrorMessage}
          isOpen={isOpenModalPaymentError}
          setIsOpen={setIsOpenModalPaymentError}
        >
          <Button newStyles={styles.button} onClickFunc={handleCloseModalPayemntError}>
            Ок
          </Button>
        </Modal>
      ) : null}
    </>
  );
};
